import InlineVariableEditor from '@/components/editor/lexical/inline-variable-editor';
import { HStack, Text } from '@chakra-ui/react';
import { VariableDropdownPlugin, type AvailableVariable } from '@fluenceur/ui';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { EMAIL_COMPOSER_ROW_LABEL_WIDTH } from './email-composer-layout';

// Aligned on the strictest backend subject validator (the collab proposal email
// caps at 255; the bulk rails accept 500), so this bound is safe on every
// surface that renders the subject row.
const SUBJECT_MAX_LENGTH = 255;

interface EmailComposerSubjectRowProps {
  resetKey: number;
  /**
   * Plain-text subject hydrating the editor on mount; `{{key}}` tokens render as
   * variable chips. Omit to start empty behind the placeholder. Read once per
   * mount — bump `resetKey` to re-hydrate from a new seed.
   */
  initialContent?: string;
  availableVariables: AvailableVariable[];
  onContentChange: (plainText: string) => void;
  /**
   * Width of the `Objet:` label cell. Defaults to the shared row width so the
   * historic modals (De/Destinataires/Programme/Objet) stay column-aligned. The
   * standalone outreach rows pass `"auto"` so the lone label hugs its text
   * instead of leaving a fixed-width gap.
   */
  labelWidth?: string;
}

function EmailComposerSubjectRow({
  resetKey,
  initialContent,
  availableVariables,
  onContentChange,
  labelWidth = EMAIL_COMPOSER_ROW_LABEL_WIDTH,
}: EmailComposerSubjectRowProps) {
  const { t } = useTranslation();

  return (
    <HStack minW={0} gap={3} align="center">
      <Text fontSize="sm" color="fg.muted" w={labelWidth} flexShrink={0}>
        {t('bulkEmail.compose.subject.label')}:
      </Text>
      <InlineVariableEditor
        key={resetKey}
        editorKey={resetKey}
        initialPlainText={initialContent}
        placeholder={t('bulkEmail.compose.subject.placeholder')}
        availableVariables={availableVariables}
        translateFn={t}
        onContentChange={onContentChange}
        fontSize="15px"
        minH="28px"
        maxLength={SUBJECT_MAX_LENGTH}
      >
        <VariableDropdownPlugin
          availableVariables={availableVariables}
          translateFn={t}
          insertVariableLabel={t('invitations.editor.insertVariable')}
          placement="bottom-end"
        />
      </InlineVariableEditor>
    </HStack>
  );
}

// Every keystroke in the body re-renders the caller; the row's props are all
// stable references, so memo keeps the subject editor out of that churn.
export default memo(EmailComposerSubjectRow);
